import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Typography, LogoSVG } from 'components/atoms';
import { NewBreadCrumb } from 'components/molecules';
import { ViewScreen } from 'components/template';
import { EmployeeReviewTable } from 'components/organisms/EmployeeReviewTable';
import { EmployeeReviewFilter } from 'components/organisms/EmployeeReviewFilter';
import { RootState, useAppDispatch, useAppSelector } from 'store';
import { getSupervisorGoalAssignments } from 'store/Admin';
import { GoalAssignmentData } from 'models/models';
import { EmployeeReviewData } from 'components/pages/EmployeeReview';
import { useCurrentEmployeeId } from 'services/hooks';
import './index.scss';

export interface SupervisorReviewData {
  id: string;
  name: string; 
  fullName: string;
  userName: string;
  percentage: number;
  status: 'pending' | 'approved' | 'ongoing' | 'completed' | 'incomplete';
  acceptanceStatus?: 'accepted' | 'declined' | 'pending' | 'mixed';
  team?: string;
  role?: string;
  avatar?: string;
  userRoleId: string;
  upn: string;
  epfNumber: string;
  designation?: string | null;
  employeeNickName: string;
  isActive: boolean;
  goalDetails?: { goalTitle: string; acceptanceStatus: 'Accepted' | 'Declined' | 'Pending' }[];
  performance?: {
    totalGoals: number;
    completedGoals: number;
    pendingGoals: number;
    inProgressGoals: number;
    acceptedGoals: number;
    declinedGoals: number;
    totalSubtasks: number;
    completedSubtasks: number;
    pendingSubtasks: number;
    inProgressSubtasks: number;
    goalsCompletionRate: number;
    subtasksCompletionRate: number;
    overallPerformanceRate: number;
  };
}

export const SupervisorReview = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(5);

  const { employeeId: supervisorId, isLoading: employeeIdLoading } = useCurrentEmployeeId();


  const { supervisorGoalAssignments, supervisorGoalAssignmentsStatus } = useAppSelector((state: RootState) => state.admin);

  const transformGoalAssignmentData = (assignments: GoalAssignmentData[]): SupervisorReviewData[] => {
    return assignments.map((assignment) => {
      const employee = assignment.employee[0];
      
      let acceptanceStatus: 'accepted' | 'declined' | 'pending' | 'mixed' = 'pending';
      let goalDetails: { goalTitle: string; acceptanceStatus: 'Accepted' | 'Declined' | 'Pending' }[] = [];
      
      if (assignment.performance) {
        const { acceptedGoals = 0, declinedGoals = 0, totalGoals = 0 } = assignment.performance;
        
        const acceptedCount = acceptedGoals;
        const declinedCount = declinedGoals;
        const pendingCount = totalGoals - acceptedCount - declinedCount;
        
        for (let i = 1; i <= acceptedCount; i++) {
          goalDetails.push({
            goalTitle: `Goal ${i}`,
            acceptanceStatus: 'Accepted'
          });
        }
        for (let i = 1; i <= declinedCount; i++) {
          goalDetails.push({
            goalTitle: `Goal ${acceptedCount + i}`,
            acceptanceStatus: 'Declined'
          });
        }
        for (let i = 1; i <= pendingCount; i++) {
          goalDetails.push({
            goalTitle: `Goal ${acceptedCount + declinedCount + i}`,
            acceptanceStatus: 'Pending'
          });
        }
        
        if (totalGoals > 0) {
          if (acceptedCount > 0 && declinedCount === 0) {
            acceptanceStatus = 'accepted';
          } else if (acceptedCount > 0 && declinedCount > 0) {
            acceptanceStatus = 'declined';
          } else if (acceptedCount === 0 && declinedCount === 0) {
            acceptanceStatus = 'pending';
          } else if (acceptedCount === 0 && declinedCount > 0) {
            acceptanceStatus = 'declined';
          } else {
            acceptanceStatus = 'pending';
          }
        }
      }
      
      return {
        id: employee?.id || '',
        name: employee?.fullName || employee?.userName || 'Unknown Employee',
        fullName: employee?.fullName || '',
        userName: employee?.userName || '',
        percentage: Math.round(assignment.performance.overallPerformanceRate),
        status: (() => {
          const { completedGoals = 0, totalGoals = 0, inProgressSubtasks = 0 } = assignment.performance;
          
          if (completedGoals === totalGoals && totalGoals > 0) {
            return 'completed';
          } else if (inProgressSubtasks > 0) {
            return 'ongoing';
          } else {
            return 'pending';
          }
        })(),
        acceptanceStatus,
        team: '',
        role: employee?.designation || 'No Designation',
        avatar: employee?.fullName ? employee.fullName.split(' ').map(n => n[0]).join('').toUpperCase() :
          employee?.userName ? employee.userName.substring(0, 2).toUpperCase() : 'NA',
        userRoleId: employee?.id || '',
        upn: employee?.email || '',
        epfNumber: employee?.epfNumber || '',
        designation: employee?.designation || null,
        employeeNickName: employee?.userName || employee?.fullName || '',
        isActive: true,
        goalDetails,
        performance: assignment.performance
      };
    });
  };

  const employeeReviews = useMemo(() => transformGoalAssignmentData(supervisorGoalAssignments), [supervisorGoalAssignments]);

  const convertToEmployeeReviewData = (scheduleData: SupervisorReviewData[]): EmployeeReviewData[] => {
    return scheduleData.map(employee => ({
      id: employee.epfNumber,
      name: employee.userName,
      percentage: employee.percentage,
      status: employee.status,
      role: employee.role,
      avatar: employee.avatar,
      userRoleId: employee.userRoleId,
      upn: employee.upn,
      epfNumber: employee.epfNumber,
      designation: employee.designation,
      employeeNickName: employee.employeeNickName,
      isActive: employee.isActive,
      acceptanceStatus: employee.acceptanceStatus,
      goalDetails: employee.goalDetails,
      performance: employee.performance
    }));
  };

  const filteredEmployees = useMemo(() => {
    if (searchTerm.trim() === '') {
      return convertToEmployeeReviewData(employeeReviews);
    } else {
      const filtered = employeeReviews.filter(
        (employee) =>
          employee.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          employee.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
          employee.role?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          employee.upn?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          employee.designation?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          employee.epfNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          employee.employeeNickName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          employee.fullName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          employee.userName?.toLowerCase().includes(searchTerm.toLowerCase())
      );
      return convertToEmployeeReviewData(filtered);
    }
  }, [employeeReviews, searchTerm]);

  const handleEmployeeClick = (employee: EmployeeReviewData) => {
    navigate(`/supervisor-review/${employee.epfNumber}/tasks`, { state: { employee } });
  };

  const handleSearch = (term: string) => {
    setSearchTerm(term);
    setCurrentPage(1);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (newItemsPerPage: number) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
  };

  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedEmployees = filteredEmployees.slice(startIndex, endIndex);

  useEffect(() => {
    if (supervisorId) {
      dispatch(getSupervisorGoalAssignments(supervisorId));
    }
  }, [dispatch, supervisorId]);

  if (employeeIdLoading || supervisorGoalAssignmentsStatus === 'pending') {
    return (
      <div className="loading-container">
        <LogoSVG />
      </div>
    );
  }

  const headerItem1 = (
    <>
      <div className='supervisor-review-header'>
        <div className="header-left">
          <Typography type="heading4" text="Supervisor Reviews" />
          <NewBreadCrumb />
        </div>
      </div>
    </>
  );

  const headerItem2 = (
    <>
      <div className="supervisor-review__actions">
        <EmployeeReviewFilter onSearch={handleSearch} searchTerm={searchTerm} />
      </div>
    </>
  );

  return (
    <ViewScreen
      headerItem1={headerItem1}
      headerItem2={headerItem2}
      mainItem2={
        <>
          <div className="supervisor-review">
            {filteredEmployees.length > 0 && (
              <div className="supervisor-review__overview">
                <div className="overview-card">
                  <Typography type="heading5" text="Employee Review Summary" />
                  <div className="summary-stats">
                    <div className="stat-item">
                      <Typography type="small_medium" text="Total Employees" />
                      <Typography type="heading5" text={employeeReviews.length.toString()} />
                    </div>
                    <div className="stat-item">
                      <Typography type="small_medium" text="Completed Reviews" />
                      <Typography type="heading5" text={employeeReviews.filter(emp => emp.status === 'completed').length.toString()} />
                    </div>
                    <div className="stat-item">
                      <Typography type="small_medium" text="Ongoing Reviews" />
                      <Typography type="heading5" text={employeeReviews.filter(emp => emp.status === 'ongoing').length.toString()} />
                    </div>
                    <div className="stat-item">
                      <Typography type="small_medium" text="Pending Reviews" />
                      <Typography type="heading5" text={employeeReviews.filter(emp => emp.status === 'pending').length.toString()} />
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div className="supervisor-review__content">
              {filteredEmployees.length > 0 && (
                <div className="content-header">
                  <Typography type="heading5" text="Employee List" />
                  <Typography type="small_regular" text="Select an employee to schedule or view their review tasks" color="gray" />
                </div>
              )}
              {filteredEmployees.length === 0 ? (
                <div className="no-employees-message">
                  <img src="/icons/employee.svg" alt="No Employees" className="no-goals-icon enhanced" />
                  <Typography
                    type="heading5"
                    text="You don't have any employees to supervise."
                    className="no-goals-title"
                  />
                  <Typography
                    type="base_regular"
                    text="When you have employees to supervise, they will appear here."
                    className="no-goals-desc"
                  />
                </div>
              ) : (
                <EmployeeReviewTable
                  data={paginatedEmployees}
                  onEmployeeClick={handleEmployeeClick}
                  showScheduleColumn={true}
                  showAcceptanceStatusColumn={true}
                  showNextReviewColumn={false}
                  showPagination={true}
                  currentPage={currentPage}
                  itemsPerPage={itemsPerPage}
                  totalItems={filteredEmployees.length}
                  onPageChange={handlePageChange}
                  onItemsPerPageChange={handleItemsPerPageChange}
                />
              )}
            </div>
          </div>
        </>
      }
      portalTitle="Supervisor Review"
    />
  );
}; 